/**
 * Giesto wishlist — a dependency-free favourites store.
 *
 * Persists to localStorage, syncs across tabs, and drives two custom elements:
 *   <giesto-wishlist-toggle>  the heart on a product card
 *   <giesto-wishlist-count>   the badge on the header heart
 *
 * A future wishlist app can take over by listening to `giesto:wishlist:change`
 * or by calling the methods on `window.GiestoWishlist` directly.
 */
(() => {
  // Several sections each load this asset, so the script can execute more than
  // once per page. Without this guard the second run builds a second store and
  // overwrites window.GiestoWishlist, while the already-registered custom
  // elements stay bound to the first — the header count then stops updating.
  if (window.GiestoWishlist) return;

  const STORAGE_KEY = 'giesto:wishlist';
  const CHANGE_EVENT = 'giesto:wishlist:change';
  const MAX_ITEMS = 250;
  const TOAST_DURATION = 5000;
  let toastTimer = 0;

  /** @typedef {{id: string, handle: string, title: string, url: string, image: string, price: string}} WishlistItem */

  /** @param {unknown} value */
  const plainText = (value) => {
    const parsedDocument = new DOMParser().parseFromString(String(value ?? ''), 'text/html');
    return parsedDocument.body.textContent?.trim() ?? '';
  };

  /** @returns {WishlistItem[]} */
  const read = (fallback = []) => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.filter((item) => item && item.id).map((item) => ({ ...item, price: plainText(item.price) }))
        : [];
    } catch {
      return fallback;
    }
  };

  /** @param {WishlistItem[]} items */
  const write = (items) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
    } catch {
      /* quota or private mode — keep the in-memory state and carry on */
    }
  };

  const wishlistCopy = () => {
    const english = document.documentElement.lang.toLowerCase().startsWith('en');
    return english
      ? {
          message: 'Item added to your saved items',
          action: 'View saved items',
          label: 'Saved item confirmation',
        }
      : {
          message: 'Ürün kaydedilenlere eklendi',
          action: 'Kaydedilenleri görüntüle',
          label: 'Ürün kaydedildi bildirimi',
        };
  };

  const savedItemsUrl = () => {
    const english = document.documentElement.lang.toLowerCase().startsWith('en');
    return `${english ? '/en' : ''}/pages/favori`;
  };

  const ensureToast = () => {
    let toast = document.querySelector('[data-giesto-wishlist-toast]');
    if (toast) return toast;

    const copy = wishlistCopy();
    toast = document.createElement('aside');
    toast.className = 'giesto-wishlist-toast';
    toast.dataset.giestoWishlistToast = '';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.setAttribute('aria-label', copy.label);
    toast.hidden = true;
    toast.innerHTML = `
      <div class="giesto-wishlist-toast__message">
        <svg class="giesto-wishlist-toast__check" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="11" fill="currentColor"></circle>
          <path d="m6.8 12.3 3.2 3.2 7.2-7.2" fill="none" stroke="#fff" stroke-width="1.8" stroke-linecap="square"></path>
        </svg>
        <span data-giesto-wishlist-toast-message></span>
      </div>
      <a class="giesto-wishlist-toast__action" data-giesto-wishlist-toast-action>
        <span>${copy.action}</span>
        <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3 10h13m-5-5 5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.4"></path></svg>
      </a>`;

    const style = document.createElement('style');
    style.dataset.giestoWishlistToastStyle = '';
    style.textContent = `
      .giesto-wishlist-toast {
        box-sizing: border-box;
        position: fixed;
        z-index: 10000;
        top: max(16px, env(safe-area-inset-top));
        right: max(16px, env(safe-area-inset-right));
        width: min(320px, calc(100vw - 32px));
        padding: 14px;
        border-radius: 6px;
        background: #fff;
        color: #111;
        box-shadow: 0 4px 12px rgb(0 0 0 / 10%);
        font-family: Arial, sans-serif;
        opacity: 0;
        transform: translateY(-8px);
        transition: opacity 180ms ease, transform 180ms ease;
      }
      .giesto-wishlist-toast[hidden] { display: none; }
      .giesto-wishlist-toast[data-visible] { opacity: 1; transform: translateY(0); }
      .giesto-wishlist-toast__message {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        width: 100%;
        font-size: 14px;
        line-height: 20px;
      }
      .giesto-wishlist-toast__check { flex: 0 0 24px; width: 24px; height: 24px; color: #38804a; }
      .giesto-wishlist-toast__message span { flex: 1; }
      .giesto-wishlist-toast__action {
        box-sizing: border-box;
        display: flex;
        width: 100%;
        min-height: 40px;
        align-items: center;
        justify-content: center;
        gap: 8px;
        margin-top: 8px;
        border: 1px solid #111;
        border-radius: 4px;
        background: #fff;
        color: #111;
        font-size: 14px;
        line-height: 20px;
        text-decoration: none;
      }
      .giesto-wishlist-toast__action:hover,
      .giesto-wishlist-toast__action:focus-visible { background: #111; color: #fff; }
      .giesto-wishlist-toast__action svg { width: 16px; height: 16px; }
      @media (prefers-reduced-motion: reduce) {
        .giesto-wishlist-toast { transition: none; }
      }`;

    document.head.append(style);
    document.body.append(toast);
    return toast;
  };

  const showAddedToast = (item) => {
    const toast = ensureToast();
    const copy = wishlistCopy();
    const message = toast.querySelector('[data-giesto-wishlist-toast-message]');
    const action = toast.querySelector('[data-giesto-wishlist-toast-action]');
    if (message) message.textContent = item?.title ? `${item.title}: ${copy.message}` : copy.message;
    if (action instanceof HTMLAnchorElement) action.href = savedItemsUrl();

    window.clearTimeout(toastTimer);
    toast.hidden = false;
    window.requestAnimationFrame(() => toast.setAttribute('data-visible', ''));
    toastTimer = window.setTimeout(() => {
      toast.removeAttribute('data-visible');
      window.setTimeout(() => {
        if (!toast.hasAttribute('data-visible')) toast.hidden = true;
      }, 180);
    }, TOAST_DURATION);
  };

  let items = read();

  /**
   * @param {WishlistItem | null} changed
   * @param {'add' | 'remove' | 'clear' | 'sync'} action
   */
  const publish = (changed, action) => {
    renderHeader();
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, {
        bubbles: true,
        detail: { items: [...items], count: items.length, changed, action },
      })
    );
  };

  // A restored page or suspended tab may have missed a storage event. Refresh
  // before every mutation so its old array cannot resurrect a removed item.
  const refresh = () => {
    const latest = read(items);
    if (JSON.stringify(latest) === JSON.stringify(items)) return;
    items = latest;
    publish(null, 'sync');
  };

  function renderHeader(root = document) {
    for (const link of root.querySelectorAll('[data-testid="header-wishlist"]')) {
      if (link.getAttribute('href') !== savedItemsUrl()) link.setAttribute('href', savedItemsUrl());
      let holder = link.querySelector(':scope > span');
      if (!holder) { holder = document.createElement('span'); link.append(holder); }
      const count = items.length;
      let badge = holder.querySelector('[data-giesto-wishlist-badge]');
      if (!count) { if (holder.childNodes.length) holder.replaceChildren(); }
      else {
        if (!badge) {
          badge = document.createElement('span');
          badge.dataset.giestoWishlistBadge = '';
          badge.setAttribute('aria-hidden', 'true');
          badge.style.cssText = 'position:absolute;top:-.4rem;right:1rem;transform:translateX(100%);display:inline-block;box-sizing:border-box;min-width:1.6rem;height:1.6rem;padding:0 .4rem;border-radius:999px;background:rgb(8,81,189);color:white;text-align:center;font:1.2rem/1.8rem var(--font-small-caps,ga-small-caps),Arial,sans-serif;letter-spacing:-.02em';
          const number = document.createElement('span');
          number.style.cssText = 'display:inline-block;transform:translateY(-.25rem)';
          badge.append(number);holder.replaceChildren(badge);
        }
        const value = count > 99 ? '99+' : String(count);
        if (badge.firstChild.textContent !== value) badge.firstChild.textContent = value;
      }
      const label = document.documentElement.lang.toLowerCase().startsWith('en') ? 'Wishlist' : 'Favoriler';
      const accessibleLabel = count ? `${label} (${count})` : label;
      if (link.getAttribute('aria-label') !== accessibleLabel) link.setAttribute('aria-label', accessibleLabel);
    }
  }

  const store = {
    refresh,
    syncHeader: renderHeader,
    get items() {
      return [...items];
    },

    get count() {
      return items.length;
    },

    /** @param {string} id */
    has(id) {
      return items.some((item) => String(item.id) === String(id));
    },

    /** @param {WishlistItem} item */
    add(item) {
      refresh();
      if (!item || !item.id || store.has(item.id)) return false;
      const normalizedItem = { ...item, id: String(item.id), price: plainText(item.price) };
      items = [normalizedItem, ...items].slice(0, MAX_ITEMS);
      write(items);
      publish(normalizedItem, 'add');
      showAddedToast(normalizedItem);
      return true;
    },

    /** @param {string} id */
    remove(id) {
      refresh();
      const next = items.filter((item) => String(item.id) !== String(id));
      if (next.length === items.length) return false;
      const removed = items.find((item) => String(item.id) === String(id)) ?? null;
      items = next;
      write(items);
      publish(removed, 'remove');
      return true;
    },

    /** @param {WishlistItem} item */
    toggle(item) {
      refresh();
      return store.has(item.id) ? (store.remove(item.id), false) : (store.add(item), true);
    },

    clear() {
      refresh();
      if (!items.length) return;
      items = [];
      write(items);
      publish(null, 'clear');
    },
  };

  window.GiestoWishlist = store;

  // Another tab changed the list — adopt it and re-render.
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY && event.key !== null) return;
    refresh();
  });

  window.addEventListener('pageshow', refresh);
  window.addEventListener('focus', refresh);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refresh(); });

  // The homepage header hydrates after this script. Keep only its badge bound
  // to the shared store; its existing React menu/layout remains the canonical UI.
  let headerFrame = 0;
  new MutationObserver(() => {
    if (headerFrame) return;
    headerFrame = requestAnimationFrame(() => { headerFrame = 0; renderHeader(); });
  }).observe(document.body, { childList: true, subtree: true });

  /**
   * The heart button on a product card.
   *
   * Renders unpressed on the server so the markup stays cacheable, then
   * corrects itself from localStorage once connected.
   */
  class GiestoWishlistToggle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;

      this.onClick = this.onClick.bind(this);
      this.render = this.render.bind(this);

      this.button.addEventListener('click', this.onClick);
      document.addEventListener(CHANGE_EVENT, this.render);
      this.render();
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onClick);
      document.removeEventListener(CHANGE_EVENT, this.render);
    }

    get item() {
      const { productId, productHandle, productTitle, productUrl, productImage, productPrice } = this.dataset;
      return {
        id: String(productId ?? ''),
        handle: productHandle ?? '',
        title: productTitle ?? '',
        url: productUrl ?? '',
        image: productImage ?? '',
        price: productPrice ?? '',
      };
    }

    /** @param {MouseEvent} event */
    onClick(event) {
      // The card is one big anchor — don't let the heart navigate.
      event.preventDefault();
      event.stopPropagation();
      store.toggle(this.item);
    }

    render() {
      if (!this.button) return;
      const active = store.has(this.dataset.productId ?? '');
      this.button.setAttribute('aria-pressed', String(active));
      this.toggleAttribute('data-active', active);

      const label = active ? this.dataset.labelRemove : this.dataset.labelAdd;
      if (label) this.button.setAttribute('aria-label', label);
    }
  }

  /** The count badge next to the header heart. */
  class GiestoWishlistCount extends HTMLElement {
    connectedCallback() {
      this.render = this.render.bind(this);
      document.addEventListener(CHANGE_EVENT, this.render);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.render);
    }

    render() {
      const { count } = store;
      this.textContent = count > 99 ? '99+' : String(count);
      this.toggleAttribute('hidden', count === 0);
      this.setAttribute('aria-hidden', 'true');

      const control = this.closest('[data-giesto-wishlist-control]');
      if (control && this.dataset.labelTemplate) {
        control.setAttribute('aria-label', this.dataset.labelTemplate.replace('{{ count }}', String(count)));
      }
    }
  }

  if (!customElements.get('giesto-wishlist-toggle')) {
    customElements.define('giesto-wishlist-toggle', GiestoWishlistToggle);
  }

  if (!customElements.get('giesto-wishlist-count')) {
    customElements.define('giesto-wishlist-count', GiestoWishlistCount);
  }
  // Headers/pages can connect before the deferred store asset has executed.
  publish(null, 'sync');
})();
